import React, { useState, useEffect, useCallback } from 'react';
import { CalendarClock, Video, RefreshCw, Phone, Building2, CheckCircle2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  no_show: { label: 'No-show', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
};

const DemoRow = ({ d, past }) => {
  const sm = STATUS_META[d.status] || STATUS_META.scheduled;
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-zinc-100 last:border-0 hover:bg-zinc-50/70 transition-colors"
      data-testid={`demo-row-${d.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-zinc-900 text-sm truncate">{d.name}</p>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${sm.cls}`} data-testid={`demo-status-${d.id}`}>{sm.label}</span>
        </div>
        <p className="text-xs text-zinc-500 tabular-nums flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {d.phone || '—'}</p>
        {d.school_name && <p className="text-[11px] text-zinc-400 truncate flex items-center gap-1"><Building2 className="w-3 h-3 shrink-0" /> {d.school_name}</p>}
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-semibold tabular-nums flex items-center justify-end gap-1 ${past ? 'text-zinc-500' : 'text-orange-600'}`} data-testid={`demo-when-${d.id}`}>
          <CalendarClock className="w-3.5 h-3.5" /> {d.when_label}
        </p>
        <div className="flex items-center justify-end gap-2 mt-1">
          {!past && (d.reminder_sent
            ? <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5" data-testid={`demo-reminder-${d.id}`}><CheckCircle2 className="w-3 h-3" /> Reminder भेजा</span>
            : <span className="text-[10px] text-zinc-400 inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> Reminder pending</span>)}
          {d.meet_link && (
            <a href={d.meet_link} target="_blank" rel="noopener noreferrer"
              data-testid={`demo-meet-${d.id}`}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
              <Video className="w-3 h-3" /> Join
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default function DemosView({ token }) {
  const [data, setData] = useState({ upcoming: [], past: [], upcoming_count: 0, past_count: 0, today_count: 0 });
  const [loading, setLoading] = useState(true);

  const fetchDemos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/demos`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setData(d);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  return (
    <div className="space-y-4" data-testid="crm-demos-view">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="demos-kpi-today">
          <p className="text-xs text-zinc-500">आज की Demos</p>
          <p className="text-2xl font-semibold text-orange-600 mt-1 tabular-nums">{data.today_count}</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="demos-kpi-upcoming">
          <p className="text-xs text-zinc-500">आने वाली Demos</p>
          <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums">{data.upcoming_count}</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="demos-kpi-past">
          <p className="text-xs text-zinc-500">बीती Demos</p>
          <p className="text-2xl font-semibold text-zinc-500 mt-1 tabular-nums">{data.past_count}</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={fetchDemos} disabled={loading} data-testid="demos-refresh-btn"
          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && data.upcoming.length === 0 && data.past.length === 0 ? (
        <div className="text-center py-10 text-sm text-zinc-400">Demos लोड हो रही हैं…</div>
      ) : (
        <>
          {/* Upcoming */}
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 bg-orange-50/60">
              <p className="text-sm font-semibold text-zinc-900">🎬 आने वाली Demos ({data.upcoming_count})</p>
              <p className="text-[11px] text-zinc-500">demo से 1 घंटा पहले lead + admin दोनों को WhatsApp reminder अपने-आप जाता है।</p>
            </div>
            {data.upcoming.length === 0
              ? <p className="text-sm text-zinc-400 text-center py-8" data-testid="demos-upcoming-empty">कोई आने वाली demo नहीं</p>
              : data.upcoming.map(d => <DemoRow key={d.id} d={d} past={false} />)}
          </div>

          {/* Past */}
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <p className="text-sm font-semibold text-zinc-900">🕘 बीती Demos ({data.past_count})</p>
            </div>
            {data.past.length === 0
              ? <p className="text-sm text-zinc-400 text-center py-8" data-testid="demos-past-empty">अभी तक कोई पुरानी demo नहीं</p>
              : data.past.map(d => <DemoRow key={d.id} d={d} past={true} />)}
          </div>
        </>
      )}
    </div>
  );
}
